'use client';

import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Loader2, AlertCircle, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import { Skeleton } from '@/components/ui/skeleton';
import { FormCombobox } from '@/components/forms/form-combobox';
import { InfiniteCombobox } from '@/components/forms/infinite-combobox';
import { useInfiniteSelect } from '@/hooks/use-infinite-select';
import { useRouter } from '@/i18n/routing';
import { useAccessibleRoute } from '@/hooks/use-route-access';
import {
  dynamicFormQueries,
  dynamicFormKeys,
  dynamicFormsApi,
  DynamicFormFieldInput
} from '@/features/dynamic-forms';
import type { DynamicForm, DynamicFormField } from '@/features/dynamic-forms';
import {
  containerTypeKeys,
  containerTypesApi
} from '@/features/container-types';
import { useCreateRegistrationMutation } from '../services/registration.mutations';
import type { Registration } from '../types';
import { buildRegistrationFormSchema } from '../schemas';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RegistrationSuccessDialog } from './registration-success-dialog';

export function RegistrationForm() {
  const t = useTranslations('registration');
  const tv = useTranslations('validation');
  const router = useRouter();
  const backTo = useAccessibleRoute();

  // ─── Active forms list & selection ───────────────────────────────────
  // Searchable, server-paginated list of selectable registration forms,
  // scoped to the current user's own forms (scope: 'mine', preserved from test).
  const formsSelect = useInfiniteSelect<DynamicForm>({
    queryKey: dynamicFormKeys.lists(),
    queryFn: (params) => dynamicFormsApi.getAll(params),
    mapOption: useCallback(
      (f: DynamicForm) => ({
        value: String(f.id),
        label: f.is_default ? `${f.name} (${t('defaultForm')})` : f.name
      }),
      [t]
    ),
    params: { type: 'registration', status: 'active', scope: 'mine' }
  });

  // The default form drives initial selection. Resolved via the type's active
  // template so it is found regardless of which page it would paginate onto.
  const { data: defaultFormData, isLoading: isDefaultFormLoading } = useQuery(
    dynamicFormQueries.activeByType('registration')
  );
  const defaultFormId = defaultFormData?.data?.id;

  const [selectedFormId, setSelectedFormId] = useState<number | null>(null);
  const effectiveFormId = selectedFormId ?? defaultFormId ?? null;

  // Show the selector when more than one form is selectable (more options on
  // the first page, or further pages still to load).
  const hasMultipleForms =
    formsSelect.options.length > 1 || formsSelect.hasNextPage;

  const {
    data: formData,
    isLoading: isFormDetailLoading,
    isError
  } = useQuery({
    ...dynamicFormQueries.detail(effectiveFormId ?? 0),
    enabled: !!effectiveFormId
  });

  const isLoading =
    isDefaultFormLoading || (!!effectiveFormId && isFormDetailLoading);

  const dynamicForm = formData?.data ?? formData;
  const defaultContainerTypeId = (dynamicForm as DynamicForm | undefined)
    ?.container_type_id;

  const containerTypesSelect = useInfiniteSelect({
    queryKey: containerTypeKeys.lists(),
    queryFn: (params) => containerTypesApi.getAll(params),
    mapOption: useCallback(
      (ct: { id: number; name: string }) => ({
        value: String(ct.id),
        label: ct.name,
        badge:
          defaultContainerTypeId != null && ct.id === defaultContainerTypeId
            ? t('defaultContainerTypeBadge')
            : undefined
      }),
      [defaultContainerTypeId, t]
    ),
    params: { is_active: true }
  });

  const fields = useMemo<DynamicFormField[]>(
    () => (dynamicForm as any)?.fields ?? [],
    [dynamicForm]
  );

  const schema = useMemo(
    () => buildRegistrationFormSchema(fields, tv),
    [fields, tv]
  );

  const defaultValues = useMemo(() => {
    const formResponses: Record<string, unknown> = {};
    for (const field of fields) {
      const key = String(field.id);
      switch (field.type) {
        case 'yes_no':
          formResponses[key] = false;
          break;
        case 'number':
          formResponses[key] = '';
          break;
        case 'selection_list':
          formResponses[key] = field.is_multi_select
            ? []
            : (field.default_value ?? '');
          break;
        default:
          formResponses[key] = '';
      }
    }
    return {
      container_type_id: (dynamicForm as DynamicForm | undefined)
        ?.container_type_id
        ? (String(
            (dynamicForm as DynamicForm).container_type_id
          ) as unknown as number)
        : ('' as unknown as number),
      form_responses: formResponses
    };
  }, [fields, dynamicForm]);

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues,
    values: fields.length > 0 ? defaultValues : undefined,
    resetOptions: { keepDirtyValues: true },
    mode: 'onBlur'
  });

  // Sequential session state: when the user enters a quantity > 1, we create
  // each container individually and reset the form between them. The session
  // remains active until `completedInSession === sessionTotal`.
  const [quantityInput, setQuantityInput] = useState('1');
  const [sessionTotal, setSessionTotal] = useState<number | null>(null);
  const [completedInSession, setCompletedInSession] = useState(0);
  const [createdReg, setCreatedReg] = useState<Registration | null>(null);
  const createMutation = useCreateRegistrationMutation();

  const sessionActive = sessionTotal !== null && sessionTotal > 1;
  const currentPosition = completedInSession + 1;

  async function onSubmit(values: Record<string, unknown>) {
    if (!dynamicForm || !effectiveFormId) return;

    // First submission of the session reads the quantity input and locks it in.
    // Subsequent submissions in the same session reuse the locked total.
    const effectiveTotal =
      sessionTotal ?? Math.max(1, Number(quantityInput) || 1);

    try {
      const result = await createMutation.mutateAsync({
        dynamic_form_id: effectiveFormId,
        container_type_id: Number((values as any).container_type_id),
        form_responses: (values as any).form_responses
      });
      const reg = ((result as any).data ?? result) as Registration;
      setSessionTotal(effectiveTotal);
      setCompletedInSession((n) => n + 1);
      setCreatedReg(reg);
    } catch {
      // handled by mutation error handler
    }
  }

  function endSession() {
    setSessionTotal(null);
    setCompletedInSession(0);
    setCreatedReg(null);
    setQuantityInput('1');
  }

  function continueSession() {
    // Reset form keeping only the container type for the next container.
    const containerTypeId = form.getValues('container_type_id');
    const blankResponses: Record<string, unknown> = {};
    for (const field of fields) {
      const key = String(field.id);
      switch (field.type) {
        case 'yes_no':
          blankResponses[key] = false;
          break;
        case 'selection_list':
          blankResponses[key] = field.is_multi_select ? [] : '';
          break;
        default:
          blankResponses[key] = '';
      }
    }
    form.reset({
      container_type_id: containerTypeId,
      form_responses: blankResponses
    });
    setCreatedReg(null);
  }

  if (isLoading) {
    return (
      <div className='mx-auto max-w-2xl space-y-6'>
        <Skeleton className='h-8 w-64' />
        <Card>
          <CardContent className='space-y-4 pt-6'>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className='space-y-2'>
                <Skeleton className='h-4 w-32' />
                <Skeleton className='h-10 w-full' />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isError || (!isLoading && (!dynamicForm || fields.length === 0))) {
    return (
      <div className='mx-auto max-w-2xl space-y-6'>
        <div className='flex items-center justify-between'>
          <h1 className='text-2xl font-bold tracking-tight'>
            {t('createRegistration')}
          </h1>
          <Button
            variant='ghost'
            onClick={() => router.push(backTo('/dashboard/registration'))}
          >
            <ArrowLeft className='me-2 h-4 w-4 rtl:rotate-180' />
            {t('cancelGoBack')}
          </Button>
        </div>
        <Card>
          <CardContent className='flex flex-col items-center gap-3 py-12 text-center'>
            <AlertCircle className='text-muted-foreground h-10 w-10' />
            <p className='text-sm font-medium'>{t('noLabFormTitle')}</p>
            <p className='text-muted-foreground max-w-sm text-xs'>
              {t('noLabFormHint')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className='mx-auto max-w-2xl space-y-6'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            <h1 className='text-2xl font-bold tracking-tight'>
              {t('createRegistration')}
            </h1>
            {sessionActive && (
              <span className='bg-primary/10 text-primary rounded-full px-3 py-1 text-sm font-medium'>
                {t('containerProgress', {
                  position: currentPosition,
                  total: sessionTotal ?? 1
                })}
              </span>
            )}
          </div>
          <Button
            variant='ghost'
            onClick={() => router.push(backTo('/dashboard/registration'))}
          >
            <ArrowLeft className='me-2 h-4 w-4 rtl:rotate-180' />
            {t('cancelGoBack')}
          </Button>
        </div>

        <div className='flex flex-wrap items-end gap-4'>
          {hasMultipleForms && (
            <div className='flex flex-col gap-1.5'>
              <Label
                htmlFor='active-form-select'
                className='text-muted-foreground flex items-center gap-1.5 text-xs'
              >
                <FileText className='h-3.5 w-3.5' />
                {t('registrationForm')}
              </Label>
              <InfiniteCombobox
                value={effectiveFormId ? String(effectiveFormId) : ''}
                onChange={(v) => setSelectedFormId(v ? Number(v) : null)}
                placeholder={t('selectForm')}
                infiniteConfig={formsSelect}
                displayValue={(dynamicForm as DynamicForm | undefined)?.name}
                className='h-9 w-auto min-w-[200px] text-sm'
              />
            </div>
          )}

          {sessionTotal === null && (
            <div className='flex flex-col gap-1.5'>
              <Label
                htmlFor='registration-quantity'
                className='text-muted-foreground text-xs'
              >
                {t('quantity')}
              </Label>
              <Input
                id='registration-quantity'
                type='number'
                min={1}
                step={1}
                value={quantityInput}
                onChange={(e) => setQuantityInput(e.target.value)}
                placeholder={t('quantityPlaceholder')}
                className='h-9 w-32 text-sm'
              />
            </div>
          )}
        </div>

        <Card key={effectiveFormId}>
          <CardContent className='pt-6'>
            <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
              <div className='space-y-4'>
                <FormCombobox
                  control={form.control}
                  name='container_type_id'
                  label={t('containerType')}
                  placeholder={t('containerTypePlaceholder')}
                  infiniteConfig={containerTypesSelect}
                  required
                />

                {fields.map((field) => (
                  <DynamicFormFieldInput
                    key={field.id}
                    field={field}
                    control={form.control}
                    name={`form_responses.${String(field.id)}`}
                  />
                ))}

                <div className='flex justify-end gap-3 pt-4'>
                  <Button
                    type='button'
                    variant='outline'
                    onClick={() =>
                      router.push(backTo('/dashboard/registration'))
                    }
                  >
                    {t('cancel')}
                  </Button>
                  <Button type='submit' disabled={createMutation.isPending}>
                    {createMutation.isPending && (
                      <Loader2 className='me-2 h-4 w-4 animate-spin' />
                    )}
                    {t('create')}
                  </Button>
                </div>
              </div>
            </Form>
          </CardContent>
        </Card>
      </div>

      {createdReg &&
        (() => {
          const total = sessionTotal ?? 1;
          const isLast = completedInSession >= total;

          const advanceOrFinish = () => {
            if (isLast) {
              endSession();
              router.push(backTo('/dashboard/registration'));
            } else {
              continueSession();
            }
          };

          return (
            <RegistrationSuccessDialog
              key={createdReg.id}
              containerNumber={createdReg.container_number}
              registrationId={createdReg.id}
              position={completedInSession}
              total={total}
              onComplete={advanceOrFinish}
              onCreateNew={
                isLast
                  ? () => {
                      endSession();
                      form.reset();
                    }
                  : undefined
              }
              onOpenChange={(open) => {
                if (!open) advanceOrFinish();
              }}
            />
          );
        })()}
    </>
  );
}
